import { Placement } from '@popperjs/core'
import { addDays, addMonths, endOfDay, endOfMonth, isAfter, isBefore, startOfDay } from 'date-fns'
import { Field, useFormikContext } from 'formik'
import React, { ReactElement, useEffect } from 'react'

import { SearchFormState } from '@components/SearchForm'
import usePriceCalendar from '@components/SearchForm/hooks/usePriceCalendar'
import { DirectionType } from '@components/TripDirection'
import config from '@config'
import bem from '@lib/bem'
import date from '@lib/date'
import { useTranslation } from '@lib/i18n'
import { required, chain, maxDate as validateMaxDate, minDate as validateMinDate } from '@lib/validators'
import DatePickerField from '@ui/DatePicker/Field'

interface DatesFieldSetProps {
  isDepartureDateDisabled?: (date: Date) => boolean
  calendarPlacement?: Placement
  searchDays: number
}

const minDate = startOfDay(new Date())
const maxDate = addMonths(endOfDay(minDate), config.searchBarDateRange)
const dateValidation = chain([validateMinDate(minDate), validateMaxDate(maxDate)])
const requiredDateValidation = chain([required, dateValidation])

const DatesFieldSet = (props: DatesFieldSetProps): ReactElement => {
  const { t } = useTranslation()
  const {
    values: { departureDate, returnDate, tripDirection },
    setFieldValue,
  } = useFormikContext<SearchFormState>()

  const isOutboundDisabled = (date: Date): boolean =>
    isBefore(date, startOfDay(new Date())) || isAfter(date, endOfMonth(maxDate))

  const isReturnDisabled = (date: Date): boolean =>
    isBefore(date, startOfDay(departureDate)) || isAfter(date, endOfMonth(maxDate))

  const { isDepartureDateDisabled = isOutboundDisabled, searchDays, calendarPlacement } = props
  const { outbound, inbound } = usePriceCalendar()

  const setDepartureTimeInReturnTrip = (departureDate: Date): void => {
    if (returnDate && isBefore(returnDate, startOfDay(departureDate))) {
      setFieldValue('returnDate', departureDate)
    }
  }
  const isOneWay = tripDirection === DirectionType.OneWay
  const isWeekly = tripDirection === DirectionType.Weekly
  const isOpenReturnTrip = tripDirection === DirectionType.OpenReturn

  useEffect(() => {
    isWeekly && setFieldValue('returnDate', addDays(departureDate, searchDays - 1))
  }, [departureDate, isWeekly, searchDays, setFieldValue])

  const weeklyLabel =
    departureDate && returnDate && isWeekly
      ? `${date.format(departureDate, 'E, d MMM')} - ${date.format(returnDate, 'E, d MMM')}`
      : null

  const showDepartureDateOnly = isOneWay || isWeekly || isOpenReturnTrip

  return (
    <div className={bem('search-form', 'dates')}>
      {showDepartureDateOnly ? (
        <Field
          component={DatePickerField}
          validate={dateValidation}
          name="departureDate"
          isTileDisabled={isDepartureDateDisabled}
          label={t('searchBar.departureDateLabel')}
          minDate={minDate}
          maxDate={maxDate}
          prices={outbound.loader.data}
          isPriceLoading={outbound.loader.isLoading}
          onToggle={outbound.setEnabled}
          inputLabel={weeklyLabel}
          calendarPlacement={calendarPlacement}
        />
      ) : (
        <div className="search-form__compound-date-select">
          <Field
            component={DatePickerField}
            validate={dateValidation}
            name="departureDate"
            isTileDisabled={isDepartureDateDisabled}
            label={t('searchBar.departureDateLabel')}
            minDate={minDate}
            maxDate={maxDate}
            onChange={setDepartureTimeInReturnTrip}
            prices={outbound.loader.data}
            isPriceLoading={outbound.loader.isLoading}
            onToggle={outbound.setEnabled}
            calendarPlacement={calendarPlacement}
          />
          <Field
            component={DatePickerField}
            validate={requiredDateValidation}
            name="returnDate"
            withIcon={false}
            initialValue={departureDate}
            isTileDisabled={isReturnDisabled}
            label={t('searchBar.returnDateLabel')}
            minDate={minDate}
            maxDate={maxDate}
            prices={inbound.loader.data}
            isPriceLoading={inbound.loader.isLoading}
            onToggle={inbound.setEnabled}
            calendarPlacement={calendarPlacement}
          />
        </div>
      )}
    </div>
  )
}

export default DatesFieldSet
